import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNavigation from './components/AdminNavigation';
import Footer from './components/Footer';
import ChapterTitleEditor from './components/chapters/ChapterTitleEditor';
import ShareContentModal from './components/chapters/ShareContentModal';
import ProjectOfflineSection from './components/ProjectOfflineSection';
import * as repository from './utils/offline/repository';
import { useOnlineOnly } from './components/OfflineHint';
import { getApiUrl, getPublicApiUrl } from './config';
import { getAccessToken, getCachedUserId, logout } from './utils/auth';
import {
  listIssuedShares,
  listReceivedShares,
  revokeShare,
} from './utils/apiContentSharing';
import { asArray, statusLabel, statusBadgeClass } from './utils/sharesFields';

function ProjectSettings() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Owner: share list
  const [shares, setShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [sharesError, setSharesError] = useState('');
  const [revokingId, setRevokingId] = useState(null);
  const [revokeError, setRevokeError] = useState('');

  // Owner: invite
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Owner: delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Non-owner: leave
  const [myShare, setMyShare] = useState(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState('');

  // RSS
  const [rssCopied, setRssCopied] = useState(false);
  const offline = useOnlineOnly();

  // Absolute: this URL is copied into podcast apps, so a relative one is useless.
  const rssUrl = getPublicApiUrl(`rss/projects/${projectId}.xml`);

  /**
   * Render what the device holds, then refresh from the server.
   *
   * Offline this page still has something to offer — the downloaded audio can
   * be removed without a connection — so a failed fetch must not replace it
   * with an error.
   */
  const fetchProject = useCallback(async () => {
    setLoading(true);
    setError('');

    const token = getAccessToken();
    if (!token) { logout(); return; }

    let cached = null;
    try {
      cached = await repository.getProject(projectId);
    } catch (e) {
      cached = null; // no account bound, or the store is unavailable
    }
    if (cached) {
      setProject(cached);
      setLoading(false);
    }

    try {
      const res = await axios.get(getApiUrl(`projects/${projectId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProject(res.data);
      setError('');
    } catch (err) {
      if (err?.response?.status === 401) { logout(); return; }
      if (err?.response?.status === 404) {
        setError('Project not found.');
        return;
      }
      // Unreachable rather than refused. That is a resting state when we are
      // offline or already have a copy to show, and a real failure otherwise.
      const unreachable = !err?.response;
      if (unreachable && (cached || navigator.onLine === false)) {
        setError('');
      } else {
        setError('Failed to load project. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  const isOwner = !!project?.owner_id && project.owner_id === getCachedUserId();

  // Owner: load share list on mount once project is loaded
  const fetchShares = useCallback(async () => {
    setSharesLoading(true);
    setSharesError('');
    try {
      // /projects/{id}/shares only returns accepted shares, so a fresh
      // invitation would not appear here. The issued listing is project-scoped
      // server-side and admits pending as well.
      const data = await listIssuedShares(projectId, 'pending,active');
      setShares(asArray(data));
    } catch {
      setSharesError('Failed to load shares.');
    } finally {
      setSharesLoading(false);
    }
  }, [projectId]);

  // Non-owner: find own received share for this project
  const fetchMyShare = useCallback(async () => {
    try {
      const data = await listReceivedShares();
      const found = asArray(data).find((e) => e.project_id === projectId || e.id === projectId);
      // Entries carry the project fields plus share_id, the revokable ID
      setMyShare(found || null);
    } catch {
      setMyShare(null);
    }
  }, [projectId]);

  useEffect(() => {
    if (!project) return;
    if (isOwner) {
      fetchShares();
    } else {
      fetchMyShare();
    }
  }, [project, isOwner, fetchShares, fetchMyShare]);

  const handleRevoke = async (shareId) => {
    setRevokingId(shareId);
    setRevokeError('');
    try {
      await revokeShare(shareId);
      await fetchShares();
    } catch {
      setRevokeError('Failed to revoke share. Please try again.');
    } finally {
      setRevokingId(null);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const token = getAccessToken();
      if (!token) { logout(); return; }
      await axios.delete(getApiUrl(`projects/${projectId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      // The server no longer has it; neither should this device. Best effort —
      // a storage failure must not turn a successful delete into an error.
      await repository.forgetProject(projectId)
        .catch((e) => console.warn('ProjectSettings: could not clear local copy', e));
      navigate('/dashboard');
    } catch (err) {
      if (err?.response?.status === 401) {
        setDeleteError('Unauthorized: Please login again.');
        logout();
      } else if (err?.response?.status === 403) {
        setDeleteError('Access denied: You are not the owner of this project.');
      } else if (err?.response?.status === 409) {
        setDeleteError('Cannot delete: Processing is in progress.');
      } else {
        setDeleteError(err?.response?.data?.message || err?.response?.data?.detail || 'Failed to delete. Please try again.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLeave = async () => {
    if (!myShare) return;
    setLeaveLoading(true);
    setLeaveError('');
    try {
      // myShare is a file-shaped object from /shares/received; share_id is the revokable ID
      const shareId = myShare.share_id || myShare.id;
      await revokeShare(shareId);
      // Leaving means losing access, so the local copy goes too (FR-017).
      await repository.forgetProject(projectId)
        .catch((e) => console.warn('ProjectSettings: could not clear local copy', e));
      navigate('/dashboard');
    } catch {
      setLeaveError('Failed to leave the project. Please try again.');
    } finally {
      setLeaveLoading(false);
    }
  };

  const copyRssUrl = async () => {
    try {
      await navigator.clipboard.writeText(rssUrl);
      setRssCopied(true);
      setTimeout(() => setRssCopied(false), 2000);
    } catch {
      // fallback: do nothing
    }
  };

  const openPodcastApp = (app) => {
    const encoded = encodeURIComponent(rssUrl);
    const withoutProtocol = rssUrl.replace(/^https?:\/\//, '');
    const urls = {
      'overcast': `overcast://x-callback-url/add?url=${encoded}`,
      'pocket-casts': `pktc://subscribe/${withoutProtocol}`,
      'castro': `castro://subscribe/${encoded}`,
      'podcast-addict': `https://podcastaddict.com/feed/${encoded}`,
    };
    if (urls[app]) window.location.href = urls[app];
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-light dark:bg-background-dark">
      <AdminNavigation />

      <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-2xl">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/projects/${projectId}/chapters`}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary mb-2 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to chapters
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Project Settings</h1>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}

        {error && !offline && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Offline, this is the only section that can do anything: every other
            control on this page talks to the server. Showing them disabled
            would be a page of dead buttons, so they are hidden entirely. */}
        {!loading && offline && (
          <div className="space-y-8">
            <ProjectOfflineSection projectId={projectId} />
          </div>
        )}

        {!loading && !error && project && !offline && (
          <div className="space-y-8">

            <ProjectOfflineSection projectId={projectId} />

            {/* ── Owner: Title ── */}
            {isOwner && (
              <section>
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Project title</h2>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <ChapterTitleEditor
                    projectId={projectId}
                    title={project.title}
                    editable
                    onTitleSaved={(newTitle) => setProject((p) => ({ ...p, title: newTitle }))}
                  />
                </div>
              </section>
            )}

            {/* ── RSS Feed (owner + non-owner) ── */}
            <section>
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Podcast feed</h2>
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Subscribe to this project's chapters in your podcast app:</p>
                <div className="space-y-2">
                  <button
                    onClick={() => openPodcastApp('overcast')}
                    className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-left text-sm font-medium"
                  >
                    🎧 Overcast
                  </button>
                  <button
                    onClick={() => openPodcastApp('pocket-casts')}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-left text-sm font-medium"
                  >
                    🎙️ Pocket Casts
                  </button>
                  <button
                    onClick={() => openPodcastApp('castro')}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-left text-sm font-medium"
                  >
                    📻 Castro
                  </button>
                  {/Android/i.test(navigator.userAgent) && (
                    <button
                      onClick={() => openPodcastApp('podcast-addict')}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-left text-sm font-medium"
                    >
                      🎵 Podcast Addict
                    </button>
                  )}
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Or copy the RSS feed URL:</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={rssUrl}
                      readOnly
                      className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={copyRssUrl}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-sm"
                    >
                      {rssCopied ? '✓' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Owner: Share list + invite ── */}
            {isOwner && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sharing</h2>
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Invite someone
                  </button>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                  {sharesLoading && (
                    <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">Loading shares…</p>
                  )}
                  {!sharesLoading && sharesError && (
                    <p className="px-4 py-4 text-sm text-red-600 dark:text-red-400">{sharesError}</p>
                  )}
                  {!sharesLoading && !sharesError && shares.length === 0 && (
                    <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">No shares yet. Invite someone to get started.</p>
                  )}
                  {!sharesLoading && !sharesError && shares.map((share) => {
                    // ProjectShareEntry exposes the revokable id as `share_id`;
                    // fall back to `id` defensively for older payload shapes.
                    const shareId = share.share_id || share.id;
                    return (
                      <div key={shareId} className="flex items-center justify-between px-4 py-3 gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{share.recipient_email}</p>
                          <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(share.status)}`}>
                            {statusLabel(share.status)}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRevoke(shareId)}
                          disabled={revokingId === shareId}
                          className="shrink-0 text-xs px-2.5 py-1 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                        >
                          {revokingId === shareId ? 'Revoking…' : 'Revoke'}
                        </button>
                      </div>
                    );
                  })}
                  {revokeError && (
                    <p className="px-4 py-2 text-xs text-red-600 dark:text-red-400">{revokeError}</p>
                  )}
                </div>
              </section>
            )}

            {/* ── Non-owner: Leave project ── */}
            {!isOwner && (
              <section>
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Access</h2>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    This project was shared with you. You can remove yourself from it at any time.
                  </p>
                  {leaveError && (
                    <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-800 dark:text-red-200">{leaveError}</p>
                    </div>
                  )}
                  <button
                    onClick={() => setShowLeaveConfirm(true)}
                    disabled={leaveLoading || !myShare}
                    className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 text-sm font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Leave project
                  </button>
                </div>
              </section>
            )}

            {/* ── Owner: Delete project ── */}
            {isOwner && (
              <section>
                <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Danger zone</h2>
                <div className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Permanently delete this project and all its chapters. This action cannot be undone.
                  </p>
                  <button
                    onClick={() => { setShowDeleteModal(true); setDeleteError(''); }}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete project
                  </button>
                </div>
              </section>
            )}

          </div>
        )}
      </div>

      {/* ── Delete confirmation modal ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Project</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                disabled={deleteLoading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Are you sure you want to delete this project and all chapters? This action cannot be undone.
            </p>
            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-200">{deleteError}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Leave confirmation modal ── */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Leave project</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to remove yourself from this project? You will lose access immediately.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={leaveLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLeave}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={leaveLoading}
              >
                {leaveLoading ? 'Leaving…' : 'Leave'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite modal ── */}
      <ShareContentModal
        isOpen={showInviteModal}
        contentType="book"
        contentId={projectId}
        onClose={() => {
          setShowInviteModal(false);
          fetchShares();
        }}
      />

      <Footer desktopOnly />
    </div>
  );
}

export default ProjectSettings;
